import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import useSWR from 'swr'
import { ArrowLeft } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import Loader from '@/components/ui/Loader'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/use-toast'
import { Api, detailKey } from './api'
import MultiImageUpload from './MultiImageUpload'
import {
  DetailResponse,
  PaintingRequestStatus,
  STATUS_LABEL,
  formatSubmitterName,
} from './types'

const LIST_URL = '/admin/ops-tools?opsToolsTab=painting-requests'

const PaintingRequestDetail: React.FC = () => {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { data, isLoading, mutate } = useSWR<DetailResponse>(
    id ? detailKey(id) : null,
  )

  const request = data?.request
  const locked = request?.status === PaintingRequestStatus.SENT

  const [styleDescription, setStyleDescription] = useState('')
  const [desc, setDesc] = useState('')
  const [imgs, setImgs] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [sending, setSending] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [sendDialogOpen, setSendDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  useEffect(() => {
    if (!request) return
    setStyleDescription(request.styleDescription || '')
    setDesc(request.desc || '')
    setImgs(request.styleReferenceImages || [])
  }, [request])

  if (isLoading && !data) return <Loader />
  if (!request) {
    return (
      <div className="flex-1 space-y-4">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(LIST_URL)}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h2 className="text-3xl font-bold tracking-tight">绘画需求详情</h2>
        </div>
        <p className="text-sm text-muted-foreground">需求不存在或已删除</p>
      </div>
    )
  }

  const onSave = async () => {
    if (locked) return
    if (!styleDescription.trim()) {
      toast({ variant: 'destructive', title: '请填写需求描述' })
      return
    }
    try {
      setSaving(true)
      await Api.update(request.id, {
        styleDescription: styleDescription.trim(),
        styleReferenceImages: imgs,
        desc: desc.trim(),
      })
      toast({
        title: '已保存',
        className: 'bg-green-500 text-white',
      })
      await mutate()
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: '保存失败',
        description: err?.message || '未知错误',
      })
    } finally {
      setSaving(false)
    }
  }

  const onSendConfirm = async () => {
    if (locked || !request) return
    try {
      setSending(true)
      await Api.update(request.id, {
        styleDescription: styleDescription.trim(),
        styleReferenceImages: imgs,
        desc: desc.trim(),
      })
      const res = await Api.sendApproval(request.id)
      setSendDialogOpen(false)
      toast({
        title: '已发起飞书审批',
        description: `instance: ${res.instanceCode}`,
        className: 'bg-green-500 text-white',
      })
      await mutate()
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: '发起失败',
        description: err?.message || '未知错误',
      })
      await mutate()
    } finally {
      setSending(false)
    }
  }

  const onDeleteConfirm = async () => {
    if (!request) return
    try {
      setDeleting(true)
      await Api.softDelete(request.id)
      setDeleteDialogOpen(false)
      toast({
        title: '已删除',
        className: 'bg-green-500 text-white',
      })
      navigate(LIST_URL)
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: '删除失败',
        description: err?.message || '未知错误',
      })
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="flex-1 space-y-4">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(LIST_URL)}
          className="shrink-0"
          title="返回"
          aria-label="返回"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h2 className="shrink-0 text-3xl font-bold tracking-tight">
          绘画需求详情
        </h2>
        <Badge
          variant={locked ? 'default' : 'secondary'}
          className="ml-2 shrink-0"
        >
          {STATUS_LABEL[request.status]}
        </Badge>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <Button
            variant="destructive"
            onClick={() => setDeleteDialogOpen(true)}
            disabled={deleting || sending}
          >
            删除
          </Button>
          {!locked ? (
            <>
              <Button
                variant="outline"
                onClick={onSave}
                disabled={saving || sending}
              >
                {saving ? '保存中...' : '保存'}
              </Button>
              <Button
                onClick={() => setSendDialogOpen(true)}
                disabled={saving || sending}
              >
                发起审批
              </Button>
            </>
          ) : null}
        </div>
      </div>

      <Dialog
        open={sendDialogOpen}
        onOpenChange={(open) => {
          if (!sending) setSendDialogOpen(open)
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>确认发起飞书审批？</DialogTitle>
            <DialogDescription>
              发起成功后内容将锁定，不可再修改。确认继续吗？
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setSendDialogOpen(false)}
              disabled={sending}
            >
              取消
            </Button>
            <Button
              type="button"
              onClick={onSendConfirm}
              disabled={sending}
            >
              {sending ? '发起中...' : '确认发起'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!deleting) setDeleteDialogOpen(open)
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>确认删除该需求？</DialogTitle>
            <DialogDescription>
              删除后列表将不再展示，确认继续吗？
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              取消
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={onDeleteConfirm}
              disabled={deleting}
            >
              {deleting ? '删除中...' : '确认删除'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="space-y-4 rounded-md border border-border p-4">
        <div className="space-y-2">
          <Label>创建人</Label>
          <div className="text-sm">{formatSubmitterName(request)}</div>
        </div>

        <div className="space-y-2">
          <Label>需求描述</Label>
          <Textarea
            value={styleDescription}
            onChange={(e) => setStyleDescription(e.target.value)}
            rows={8}
            disabled={locked}
            className="whitespace-pre-wrap"
          />
        </div>

        <MultiImageUpload
          value={imgs}
          onChange={setImgs}
          disabled={locked}
          label="参考图"
        />

        <div className="space-y-2">
          <Label>备注 desc（仅后台）</Label>
          <Textarea
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            rows={3}
            disabled={locked}
          />
        </div>

        <div className="grid gap-1 text-xs text-muted-foreground">
          <div>
            创建时间：
            {request.updateTime
              ? new Date(request.updateTime).toLocaleString()
              : '—'}
          </div>
          <div>
            发起时间：
            {request.publishTime
              ? new Date(request.publishTime).toLocaleString()
              : '—'}
          </div>
          {request.feishuInstanceCode ? (
            <div>飞书 instance：{request.feishuInstanceCode}</div>
          ) : null}
          {request.feishuSendError ? (
            <div className="text-destructive">
              上次发起错误：{request.feishuSendError}
            </div>
          ) : null}
          {locked ? (
            <div>已发起审批，内容已锁定</div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default PaintingRequestDetail
